/**
 * Commerce > Orders. Unified order list across every channel.
 *
 * Day-1 data is Faire orders sourced from commerce_orders (dual-written by
 * the Faire poller). Filters, multi-select bulk ship, infinite scroll, Add
 * Order modal, and formatted Excel export are all wired against the single
 * commerce orders Lambda.
 */

import { useCallback, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ToolPinToggle } from '@/components/ToolPinToggle';
import { Pill } from '@/components/Pill';
import {
  Activity,
  Download,
  ExternalLink,
  Plus,
  RefreshCw,
  Truck,
  Loader2,
  X,
} from 'lucide-react';

import { useSession } from '@/lib/useSession';
import { hasFaireAdmin, hasFaireViewer } from '@/lib/useFaireRuns';
import {
  downloadOrdersXlsx,
  faireOrderUrl,
  formatMoney,
  formatOrderNumber,
  useCreateOrder,
  useOrderFilters,
  useOrders,
  useRunSync,
  useShipBulk,
  useShipOne,
  useSyncStatus,
  type CreateOrderBody,
  type OrdersFilters,
  type OrderRow,
  type OrderStatus,
  type ShipStatus,
  type SyncStatus,
  ORDER_STATUS_LABELS,
  SHIP_STATUS_LABELS,
} from '@/lib/useOrders';
import { DataGridShell } from '@/components/grid/DataGridShell';
import { OrderActions } from './OrderActions';
import { MatchCustomerModal } from './MatchCustomerModal';
import { SearchableSelect } from '@/components/SearchableSelect';
import { InfoTooltip, SortableHeader, gridTheadClassName, type SortDir } from '@ampersandbrands/ui';
import { ClearableSearchInput } from '@/components/ClearableSearchInput';
import { formatDateShort, formatDateTime, formatInstantDate } from '@/lib/dateUtils';

// Whitelist of sortable column keys for Orders. The string values match
// the `sort=<key>:asc|desc` payload the Lambda whitelists; the union
// restricts what the UI can ever send. Keep in sync with
// SORTABLE_COLUMNS in tools/commerce/lambdas/orders/index.ts.
type SortableOrderColumn =
  | 'source_order_id'
  | 'order_date'
  | 'required_ship_date'
  | 'partner_name'
  | 'source_channel'
  | 'brand'
  | 'status'
  | 'ship_status'
  | 'total_amount';

const SORTABLE_ORDER_COLUMNS: ReadonlySet<SortableOrderColumn> = new Set<SortableOrderColumn>([
  'source_order_id',
  'order_date',
  'required_ship_date',
  'partner_name',
  'source_channel',
  'brand',
  'status',
  'ship_status',
  'total_amount',
]);

function parseSortParam(
  raw: string | null,
): { column: SortableOrderColumn; dir: SortDir } | null {
  if (!raw) return null;
  const [col, dir] = raw.split(':');
  if (!col || !dir) return null;
  if (!SORTABLE_ORDER_COLUMNS.has(col as SortableOrderColumn)) return null;
  if (dir !== 'asc' && dir !== 'desc') return null;
  return { column: col as SortableOrderColumn, dir };
}

const STATUS_TONES: Record<OrderStatus, string> = {
  cancelled:        'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  received:         'bg-amp-cream-warm text-amp-ink dark:bg-amp-dark-surface-warm dark:text-amp-dark-ink',
  ready_to_sync:    'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  sync_in_progress: 'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300',
  in_sage:          'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  allocated:        'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300',
  shipped:          'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  invoiced:         'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  delivered:        'bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300',
  manual_draft:     'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
};

// Ship status tones per COCKPIT.md section 4.2. Every value gets a pill so
// the column is never blank. Gray for muted (not_ready, suppressed, n_a),
// amber for action-available (eligible), green for success (pushed), red
// for failure (failed). "Not needed" is italicized to set it apart from Not Ready.
const SHIP_TONES: Record<ShipStatus, string> = {
  not_ready:  'bg-gray-100 text-gray-600 dark:bg-gray-800/60 dark:text-gray-300',
  eligible:   'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  pushed:     'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  failed:     'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  suppressed: 'bg-gray-100 text-gray-600 dark:bg-gray-800/60 dark:text-gray-300',
  n_a:        'bg-gray-100 text-gray-600 italic dark:bg-gray-800/60 dark:text-gray-300',
};

function StatusPill({ status }: { status: OrderStatus }) {
  return <Pill tone={STATUS_TONES[status]}>{labelStatus(status)}</Pill>;
}

// Renders a pill for every ship_status value. Same shared Pill base as
// StatusPill so the two columns line up visually. Labels and tones come
// from COCKPIT.md section 4.2.
function ShipPill({ status }: { status: ShipStatus }) {
  return <Pill tone={SHIP_TONES[status]}>{labelShip(status)}</Pill>;
}

function labelStatus(s: OrderStatus): string {
  return ORDER_STATUS_LABELS[s] ?? s;
}

function labelShip(s: ShipStatus): string {
  return SHIP_STATUS_LABELS[s] ?? s;
}

function labelChannel(v: string): string {
  switch (v) {
    case 'faire': return 'Faire';
    case 'shopify_hammonds': return "Shopify (Hammond's)";
    case 'shopify_lp': return 'Shopify (L&P)';
    case 'wholesale_edi': return 'Wholesale EDI';
    case 'counterpoint': return 'CounterPoint';
    case 'factory_store': return 'Factory store';
    case 'connect': return 'Connect';
    case 'direct': return 'Direct / phone';
    default: return v;
  }
}

function labelBrand(v: string): string {
  switch (v) {
    case 'hammonds': return "Hammond's";
    case 'lolli_pops': return 'Lolli & Pops';
    case 'ampersand': return 'Ampersand';
    default: return v;
  }
}

// Filter state lives in the URL's search params so that navigating into the
// detail page and back leaves the list view exactly as the user left it. The
// `useSearchParams` hook gives us a setter that integrates with the browser
// history stack.
function readArray(sp: URLSearchParams, key: string): string[] {
  const v = sp.get(key);
  return v ? v.split(',').filter(Boolean) : [];
}
function writeArray(sp: URLSearchParams, key: string, vals: string[]) {
  if (vals.length === 0) sp.delete(key);
  else sp.set(key, vals.join(','));
}
function writeString(sp: URLSearchParams, key: string, val: string) {
  if (!val) sp.delete(key);
  else sp.set(key, val);
}


export function CommerceOrders() {
  const { user } = useSession();
  const groups = user?.groups ?? [];
  const isViewer = hasFaireViewer(groups, user?.superAdmin);
  const isAdmin = hasFaireAdmin(groups, user?.superAdmin);

  const [searchParams, setSearchParams] = useSearchParams();

  // No default date filter. The Lambda enforces the 2026-05-18 floor on
  // every read so the floor is unavoidable; anything within that range is
  // shown by default until the user filters further.

  const q = searchParams.get('q') ?? '';
  const from = searchParams.get('from') ?? '';
  const to = searchParams.get('to') ?? '';
  const channel = readArray(searchParams, 'channel');
  const brand = readArray(searchParams, 'brand');
  const partner = readArray(searchParams, 'partner');
  const status = readArray(searchParams, 'status');
  const shipStatus = readArray(searchParams, 'ship_status');

  const updateParams = useCallback(
    (mutate: (sp: URLSearchParams) => void) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          mutate(next);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const setQ = (v: string) => updateParams((sp) => writeString(sp, 'q', v));
  const setFrom = (v: string) => updateParams((sp) => writeString(sp, 'from', v));
  const setTo = (v: string) => updateParams((sp) => writeString(sp, 'to', v));
  const setChannel = (v: string[]) => updateParams((sp) => writeArray(sp, 'channel', v));
  const setBrand = (v: string[]) => updateParams((sp) => writeArray(sp, 'brand', v));
  const setPartner = (v: string[]) => updateParams((sp) => writeArray(sp, 'partner', v));
  const setStatus = (v: string[]) => updateParams((sp) => writeArray(sp, 'status', v));
  const setShipStatus = (v: string[]) =>
    updateParams((sp) => writeArray(sp, 'ship_status', v));
  const resetFilters = () =>
    updateParams((sp) => {
      ['q', 'from', 'to', 'channel', 'brand', 'partner', 'status', 'ship_status'].forEach((k) =>
        sp.delete(k),
      );
    });

  // Sort state is URL-encoded as `sort=<column>:asc|desc` so a refresh
  // preserves the chosen order and so the backend can apply the order
  // before paginating. Client-side sort over a cursor-paginated list
  // produces inconsistent ordering across pages, which is why the sort
  // moves to the backend here. Tri-state cycle: asc -> desc -> off (param
  // removed).
  const sort = parseSortParam(searchParams.get('sort'));
  const sortBy: SortableOrderColumn | null = sort?.column ?? null;
  const sortDir: SortDir = sort?.dir ?? 'asc';
  const toggleSort = (col: SortableOrderColumn) => {
    if (sortBy !== col) {
      updateParams((sp) => sp.set('sort', `${col}:asc`));
    } else if (sortDir === 'asc') {
      updateParams((sp) => sp.set('sort', `${col}:desc`));
    } else {
      updateParams((sp) => sp.delete('sort'));
    }
  };

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [addOpen, setAddOpen] = useState(false);
  const [toast, setToast] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  const [exporting, setExporting] = useState(false);
  const [matchOrderId, setMatchOrderId] = useState<string | null>(null);
  const [statusOpen, setStatusOpen] = useState(false);
  const [syncResultOpen, setSyncResultOpen] = useState(false);
  const runSync = useRunSync();

  const filters: OrdersFilters = useMemo(
    () => ({
      q: q || undefined,
      from: from || undefined,
      to: to || undefined,
      channel: channel.length > 0 ? channel : undefined,
      brand: brand.length > 0 ? brand : undefined,
      partner: partner.length > 0 ? partner : undefined,
      status: status.length > 0 ? status : undefined,
      ship_status: shipStatus.length > 0 ? shipStatus : undefined,
      // Server-side sort. `sort` is undefined when the operator has
      // cleared it; the Lambda then falls back to the default
      // (order_date DESC, order_id DESC).
      sort: sortBy ? `${sortBy}:${sortDir}` : undefined,
      limit: 50,
    }),
    [q, from, to, channel, brand, partner, status, shipStatus, sortBy, sortDir],
  );

  const ordersQ = useOrders(filters);
  const filtersQ = useOrderFilters();
  const shipOne = useShipOne();
  const shipBulk = useShipBulk();

  if (!isViewer) {
    return <AccessDeniedCard />;
  }

  // Rows come back already sorted by the Lambda; no client-side reorder.
  const rows = ordersQ.rows;
  const selectableIds = rows
    .filter((r) => r.ship_status === 'failed' || (r.ship_status === 'eligible' && r.status !== 'shipped'))
    .map((r) => r.order_id);
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selected.has(id));

  const toggleSelect = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleSelectAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(selectableIds));
  };

  const onShipRow = (orderId: string) => {
    setToast(null);
    shipOne.mutate(orderId, {
      onSuccess: (res) => {
        setToast(
          res.ok
            ? { kind: 'ok', text: `Shipped ${orderId.slice(0, 8)}.` }
            : { kind: 'err', text: `Ship failed: ${res.error ?? 'unknown'}` },
        );
      },
      onError: (err) => setToast({ kind: 'err', text: `Ship failed: ${err.message}` }),
    });
  };

  const onBulkShip = () => {
    if (selected.size === 0) return;
    setToast(null);
    const ids = Array.from(selected);
    shipBulk.mutate(ids, {
      onSuccess: (res) => {
        const okCount = res.results.filter((r) => r.ok).length;
        setToast({
          kind: okCount === res.results.length ? 'ok' : 'err',
          text: `Shipped ${okCount} of ${res.results.length}.`,
        });
        setSelected(new Set());
      },
      onError: (err) => setToast({ kind: 'err', text: `Bulk ship failed: ${err.message}` }),
    });
  };

  const onExport = async () => {
    setExporting(true);
    setToast(null);
    try {
      await downloadOrdersXlsx(filters);
      setToast({ kind: 'ok', text: 'Export downloaded.' });
    } catch (err) {
      setToast({ kind: 'err', text: err instanceof Error ? err.message : String(err) });
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="h-full flex flex-col min-h-0">
      <div className="flex flex-wrap items-end justify-between mb-6 gap-3">
        <div>
          <p className="text-xs uppercase tracking-wide text-amp-olive dark:text-amp-olive-dark mb-1">
            Commerce
          </p>
          <h1 className="text-2xl font-semibold text-amp-ink dark:text-amp-dark-ink">Orders</h1>
          <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
            Every sales order across every channel.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <ToolPinToggle toolSlug="orders" toolName="Orders" />
          <button
            type="button"
            onClick={() => setStatusOpen(true)}
            title="Sync status (last poller, Sage mirror freshness, order counts)"
            className="inline-flex items-center gap-1 text-xs border border-amp-line rounded px-3 py-1.5 text-amp-ink hover:bg-amp-cream-warm dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm"
          >
            <Activity className="h-3 w-3" /> Status
          </button>
          <button
            type="button"
            onClick={onExport}
            disabled={exporting}
            className="inline-flex items-center gap-1 text-xs border border-amp-line rounded px-3 py-1.5 text-amp-ink hover:bg-amp-cream-warm disabled:cursor-not-allowed disabled:opacity-60 dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm"
          >
            {exporting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}
            Export to Excel
          </button>
          {isAdmin && (
            <button
              type="button"
              onClick={() => {
                setToast(null);
                runSync.mutate(undefined, {
                  onSuccess: () => setSyncResultOpen(true),
                  onError: (e) =>
                    setToast({ kind: 'err', text: `Sync failed: ${e.message}` }),
                });
              }}
              disabled={runSync.isPending}
              title="Run the Faire-to-Sage sync and download the pipefile"
              className="inline-flex items-center gap-1 text-xs border border-amp-line rounded px-3 py-1.5 text-amp-ink hover:bg-amp-cream-warm disabled:cursor-not-allowed disabled:opacity-60 dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm"
            >
              {runSync.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
              {runSync.isPending ? 'Syncing...' : 'Sync to Sage'}
            </button>
          )}
          <button
            type="button"
            onClick={() => setAddOpen(true)}
            className="inline-flex items-center gap-1 text-xs border border-amp-ink bg-amp-ink text-amp-white rounded px-3 py-1.5 hover:opacity-90 dark:border-amp-dark-ink dark:bg-amp-dark-ink dark:text-amp-dark-surface"
          >
            <Plus className="h-3 w-3" /> Add Order
          </button>
        </div>
      </div>

      {toast && (
        <div
          className={`mb-4 rounded-md border px-3 py-2 text-sm ${
            toast.kind === 'ok'
              ? 'border-green-300 bg-green-50 text-green-800 dark:border-green-800 dark:bg-green-900/40 dark:text-green-200'
              : 'border-red-300 bg-red-50 text-red-800 dark:border-red-800 dark:bg-red-900/40 dark:text-red-200'
          }`}
          role="status"
        >
          {toast.text}
        </div>
      )}

      {isAdmin && selected.size > 0 && (
        <div className="mb-3 flex items-center justify-between rounded-md border border-amp-line bg-amp-cream-warm px-3 py-2 dark:border-amp-dark-line dark:bg-amp-dark-surface-warm">
          <span className="text-sm text-amp-ink dark:text-amp-dark-ink">{selected.size} selected</span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setSelected(new Set())}
              className="text-xs border border-amp-line rounded px-3 py-1 text-amp-ink hover:bg-amp-white dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface"
            >
              Clear
            </button>
            <button
              type="button"
              onClick={onBulkShip}
              disabled={shipBulk.isPending}
              className="text-xs border border-amp-ink bg-amp-ink text-amp-white rounded px-3 py-1 hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60 dark:border-amp-dark-ink dark:bg-amp-dark-ink dark:text-amp-dark-surface inline-flex items-center gap-1"
            >
              {shipBulk.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Truck className="h-3 w-3" />}
              {shipBulk.isPending ? 'Shipping...' : `Ship selected (${selected.size})`}
            </button>
          </div>
        </div>
      )}

      <DataGridShell
        total={ordersQ.totalCount}
        loaded={rows.length}
        countNoun="orders"
        toolbar={
          <FiltersBar
            q={q}
            from={from}
            to={to}
            channel={channel}
            brand={brand}
            partner={partner}
            status={status}
            shipStatus={shipStatus}
            options={filtersQ.options}
            onQ={setQ}
            onFrom={setFrom}
            onTo={setTo}
            onChannel={setChannel}
            onBrand={setBrand}
            onPartner={setPartner}
            onStatus={setStatus}
            onShipStatus={setShipStatus}
            onReset={resetFilters}
          />
        }
        onReachEnd={ordersQ.fetchNextPage}
        hasMore={ordersQ.hasNextPage}
        isLoadingMore={ordersQ.isFetchingNextPage}
      >
        <table className="min-w-full text-sm">
          <thead className={gridTheadClassName}>
            <tr>
              {isAdmin && (
                <th className="text-left font-medium px-2 py-2 w-8">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleSelectAll}
                    aria-label="Select all eligible"
                  />
                </th>
              )}
              <SortableHeader
                label="Channel / Sage Order #"
                labelNode={<><span className="whitespace-nowrap">Channel/Sage</span><br /><span className="whitespace-nowrap">Order #</span></>}
                sorted={sortBy === 'source_order_id' ? sortDir : null}
                onToggle={() => toggleSort('source_order_id')}
                tooltip="The source channel's order ID (top) and the Sage SO number once Sage assigns one (bottom, '--' until then). Channel orders start as BO_xxx for Faire and similar for future channels. Sort runs on the channel order ID; the Sage SO number is decorative."
                thClassName="px-2 py-2"
              />
              <SortableHeader
                label="Order Date"
                sorted={sortBy === 'order_date' ? sortDir : null}
                onToggle={() => toggleSort('order_date')}
                thClassName="px-2 py-2"
              />
              <SortableHeader
                label="Ship By"
                sorted={sortBy === 'required_ship_date' ? sortDir : null}
                onToggle={() => toggleSort('required_ship_date')}
                tooltip="Ship-by date from Faire (required_ship_date) — the date by which the retailer expects the order to ship. Sort ascending to work the queue in deadline order."
                thClassName="px-2 py-2"
              />
              <th className="text-left font-medium px-2 py-2"><span className="whitespace-nowrap">Shipped/</span><br /><span className="whitespace-nowrap">Delivered</span></th>
              <SortableHeader
                label="Partner"
                sorted={sortBy === 'partner_name' ? sortDir : null}
                onToggle={() => toggleSort('partner_name')}
                thClassName="px-2 py-2"
              />
              <SortableHeader
                label="Channel"
                sorted={sortBy === 'source_channel' ? sortDir : null}
                onToggle={() => toggleSort('source_channel')}
                thClassName="px-2 py-2"
              />
              <SortableHeader
                label="Brand"
                sorted={sortBy === 'brand' ? sortDir : null}
                onToggle={() => toggleSort('brand')}
                thClassName="px-2 py-2"
              />
              <SortableHeader
                label="Status"
                sorted={sortBy === 'status' ? sortDir : null}
                onToggle={() => toggleSort('status')}
                tooltip="Where the order is in its journey from the source channel through Sage. Received -> Ready to Sync -> Syncing -> In Sage -> Shipped. See cockpit-ops for details."
                thClassName="px-2 py-2"
              />
              <SortableHeader
                label="Ship"
                sorted={sortBy === 'ship_status' ? sortDir : null}
                onToggle={() => toggleSort('ship_status')}
                tooltip="Status of sending tracking and freight back to the source channel after Sage invoices. Most rows show Not Ready until the warehouse invoices the order in Sage."
                thClassName="px-2 py-2"
              />
              <SortableHeader
                label="Total"
                sorted={sortBy === 'total_amount' ? sortDir : null}
                onToggle={() => toggleSort('total_amount')}
                align="right"
                thClassName="px-2 py-2"
              />
              <th className="text-left font-medium px-2 py-2">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-amp-line dark:divide-amp-dark-line">
            {ordersQ.isLoading ? (
              <tr>
                <td colSpan={isAdmin ? 12 : 11} className="px-4 py-6 text-amp-ink-muted dark:text-amp-dark-ink-muted">
                  Loading orders...
                </td>
              </tr>
            ) : ordersQ.error ? (
              <tr>
                <td colSpan={isAdmin ? 12 : 11} className="px-4 py-6 text-red-700 dark:text-red-300">
                  Could not load orders: {ordersQ.error.message}
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={isAdmin ? 12 : 11} className="px-4 py-6 text-amp-ink-muted dark:text-amp-dark-ink-muted">
                  No orders match this filter.
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <OrderListRow
                  key={row.order_id}
                  row={row}
                  selected={selected.has(row.order_id)}
                  onToggle={() => toggleSelect(row.order_id)}
                  isAdmin={isAdmin}
                  isEditor={isViewer}
                  shippingOrderId={
                    shipOne.isPending && typeof shipOne.variables === 'string'
                      ? shipOne.variables
                      : null
                  }
                  onShip={onShipRow}
                  onMatch={(id) => setMatchOrderId(id)}
                />
              ))
            )}
          </tbody>
        </table>
      </DataGridShell>

      {addOpen && (
        <AddOrderModal
          onClose={() => setAddOpen(false)}
          onCreated={(orderId) => {
            setAddOpen(false);
            setToast({ kind: 'ok', text: `Order ${orderId.slice(0, 8)} created as manual draft.` });
            ordersQ.refetch();
          }}
        />
      )}

      {matchOrderId && (
        <MatchCustomerModal
          orderId={matchOrderId}
          sourceOrderId={rows.find((r) => r.order_id === matchOrderId)?.source_order_id ?? null}
          onClose={() => setMatchOrderId(null)}
          onMatched={(_customerNo, name) => {
            setToast({
              kind: 'ok',
              text: name ? `Matched to ${name}.` : 'Customer matched.',
            });
            ordersQ.refetch();
          }}
        />
      )}

      {statusOpen && <StatusModal onClose={() => setStatusOpen(false)} />}

      {syncResultOpen && runSync.data && (
        <SyncResultModal
          result={runSync.data}
          onClose={() => setSyncResultOpen(false)}
        />
      )}
    </div>
  );
}

function StatusModal({ onClose }: { onClose: () => void }) {
  const q = useSyncStatus();
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4" role="dialog">
      <div className="relative w-full max-w-xl rounded-lg border border-amp-line bg-amp-white p-5 shadow-2 dark:border-amp-dark-line dark:bg-amp-dark-surface">
        <button
          type="button"
          onClick={onClose}
          className="absolute right-3 top-3 text-amp-ink-muted hover:text-amp-ink dark:text-amp-dark-ink-muted dark:hover:text-amp-dark-ink"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>
        <h2 className="text-lg font-semibold text-amp-ink dark:text-amp-dark-ink mb-1">
          Sync status
        </h2>
        <p className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted mb-4">
          Click the question mark next to any label for a short explanation.
        </p>
        {q.isLoading ? (
          <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">Loading...</p>
        ) : q.error ? (
          <p className="text-sm text-red-700 dark:text-red-300">{q.error.message}</p>
        ) : q.data ? (
          <StatusBody data={q.data} />
        ) : null}
        <div className="mt-4 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="text-xs border border-amp-line rounded px-3 py-1.5 text-amp-ink hover:bg-amp-cream-warm dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function StatusBody({ data }: { data: SyncStatus }) {
  // Labels and one-sentence explanations come from COCKPIT.md section
  // 4.3 (the canonical source). Update there first, then mirror here.
  return (
    <div className="space-y-5">
      <section>
        <h3 className="text-xs uppercase tracking-wide text-amp-olive dark:text-amp-olive-dark mb-2">
          Pipeline freshness
        </h3>
        <div className="text-sm space-y-1">
          <LabeledValue
            label="Last channel pull"
            tooltip="Timestamp of the most recent successful poller run across all sales channels."
          >
            {formatDateTime(data.poller_last_run_at)}
          </LabeledValue>
          <LabeledValue
            label="Last pipefile delivered to Sage server"
            tooltip="Timestamp of the most recent successful otto-it runner write to the Sage SMB share."
          >
            {formatDateTime(data.runner_last_pipefile_at)}
          </LabeledValue>
          <LabeledValue
            label="Last Sage data refresh"
            tooltip="Most recent successful Cockpit Sage Sync cycle. Falls back to the across-mirror MAX(DateUpdated) until csv-loader has populated csv_loader_runs."
          >
            {formatDateTime(data.sage_mirror_last_refreshed_at)}
          </LabeledValue>
        </div>
      </section>
      <section>
        <h3 className="text-xs uppercase tracking-wide text-amp-olive dark:text-amp-olive-dark mb-2">
          Work in flight
        </h3>
        <div className="text-sm space-y-1">
          <LabeledValue
            label="Orders waiting on customer match"
            tooltip="Orders that arrived from a channel but have not been matched to a Sage customer yet. Open the order to match it before it can sync."
          >
            <span className="font-mono">{String(data.orders_pending_match)}</span>
          </LabeledValue>
          <LabeledValue
            label="Orders waiting to import into Sage"
            tooltip="Matched orders queued for the next pipefile cycle. They move to Sage when the next pipefile is imported."
          >
            <span className="font-mono">{String(data.orders_ready_to_sync)}</span>
          </LabeledValue>
          <LabeledValue
            label="Tracking sends ready"
            tooltip="Rows currently eligible to send tracking back to the channel. Matches the Ship column when filtered to Eligible."
          >
            <span className="font-mono">{String(data.shipments_eligible)}</span>
          </LabeledValue>
          <LabeledValue
            label="Faire invoices in last 24h"
            tooltip="Count of distinct Sage invoices created in the last 24 hours that link to a Faire order. The Cockpit Sage Sync mirrors invoices every 4 hours; this can lag real Sage activity by up to that window."
          >
            <span className="font-mono">{String(data.last_sage_invoice_count_24h)}</span>
          </LabeledValue>
        </div>
      </section>
    </div>
  );
}

function LabeledValue({
  label,
  tooltip,
  children,
}: {
  label: string;
  tooltip: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-baseline gap-2 text-sm">
      <span className="w-72 shrink-0 text-amp-ink-muted dark:text-amp-dark-ink-muted inline-flex items-baseline">
        {label}
        <InfoTooltip label={label}>{tooltip}</InfoTooltip>
      </span>
      <span className="flex-1 text-amp-ink dark:text-amp-dark-ink break-words">
        {children}
      </span>
    </div>
  );
}

function SyncResultModal({
  result,
  onClose,
}: {
  result: import('@/lib/useOrders').SyncResult;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4" role="dialog">
      <div className="relative w-full max-w-md rounded-lg border border-amp-line bg-amp-white p-5 shadow-2 dark:border-amp-dark-line dark:bg-amp-dark-surface">
        <button
          type="button"
          onClick={onClose}
          className="absolute right-3 top-3 text-amp-ink-muted hover:text-amp-ink dark:text-amp-dark-ink-muted dark:hover:text-amp-dark-ink"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>
        <h2 className="text-lg font-semibold text-amp-ink dark:text-amp-dark-ink mb-3">
          Sync complete
        </h2>
        <div className="text-sm space-y-1 mb-4">
          <KV k="Orders pulled">{String(result.orders_pulled)}</KV>
          <KV k="In pipefile">{String(result.orders_in_pipefile)}</KV>
          <KV k="Skipped">{String(result.orders_skipped)}</KV>
          <KV k="Advanced to Sync In Progress">{String(result.rows_advanced)}</KV>
          {result.run_id && (
            <KV k="Run">
              <span className="font-mono text-xs">{result.run_id.slice(0, 8)}</span>
            </KV>
          )}
        </div>
        <p className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted mb-4">
          Download the pipefile and upload it to Sage via VIWI33. Once Sage assigns a Sales Order
          number, the rows will transition to "In Sage" automatically on the next Sage mirror sync.
        </p>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="text-xs border border-amp-line rounded px-3 py-1.5 text-amp-ink hover:bg-amp-cream-warm dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm"
          >
            Close
          </button>
          {result.file_url ? (
            <a
              href={result.file_url}
              className="inline-flex items-center gap-1 text-xs border border-amp-ink bg-amp-ink text-amp-white rounded px-3 py-1.5 hover:opacity-90 dark:border-amp-dark-ink dark:bg-amp-dark-ink dark:text-amp-dark-surface"
            >
              <Download className="h-3 w-3" /> Download pipefile
            </a>
          ) : (
            <span className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">
              No file generated for this run.
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function KV({ k, children }: { k: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-2 text-sm">
      <span className="w-40 shrink-0 text-amp-ink-muted dark:text-amp-dark-ink-muted">{k}</span>
      <span className="flex-1 text-amp-ink dark:text-amp-dark-ink break-words">{children}</span>
    </div>
  );
}


// --- Filters bar -------------------------------------------------------------

type FiltersBarProps = {
  q: string;
  from: string;
  to: string;
  channel: string[];
  brand: string[];
  partner: string[];
  status: string[];
  shipStatus: string[];
  options: ReturnType<typeof useOrderFilters>['options'];
  onQ: (v: string) => void;
  onFrom: (v: string) => void;
  onTo: (v: string) => void;
  onChannel: (v: string[]) => void;
  onBrand: (v: string[]) => void;
  onPartner: (v: string[]) => void;
  onStatus: (v: string[]) => void;
  onShipStatus: (v: string[]) => void;
  onReset: () => void;
};

function FiltersBar(p: FiltersBarProps) {
  const isAnyFilterActive =
    !!p.q ||
    !!p.from ||
    !!p.to ||
    p.channel.length > 0 ||
    p.brand.length > 0 ||
    p.partner.length > 0 ||
    p.status.length > 0 ||
    p.shipStatus.length > 0;

  return (
    <div className="flex flex-wrap gap-3 mb-4 items-end">
      <div className="flex-1 min-w-[220px]">
        <label htmlFor="search" className="block text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted mb-1">
          Search
        </label>
        <ClearableSearchInput
          id="search"
          value={p.q}
          onChange={p.onQ}
          placeholder="Order #, partner, Sage SO..."
          className="w-full border border-amp-line rounded pl-3 py-1.5 text-sm bg-amp-white text-amp-ink placeholder:text-amp-ink-muted dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink dark:placeholder:text-amp-dark-ink-muted"
        />
      </div>
      <DateInput id="from" label="From" value={p.from} onChange={p.onFrom} />
      <DateInput id="to" label="To" value={p.to} onChange={p.onTo} />
      <MultiSelect
        id="channel"
        label="Channel"
        value={p.channel}
        onChange={p.onChannel}
        options={p.options?.channels ?? []}
      />
      <MultiSelect
        id="brand"
        label="Brand"
        value={p.brand}
        onChange={p.onBrand}
        options={p.options?.brands ?? []}
      />
      <PartnerFilter
        value={p.partner}
        onChange={p.onPartner}
        options={p.options?.partners ?? []}
      />
      <MultiSelect
        id="status"
        label="Status"
        value={p.status}
        onChange={p.onStatus}
        options={p.options?.statuses ?? []}
      />
      <MultiSelect
        id="ship_status"
        label="Ship"
        value={p.shipStatus}
        onChange={p.onShipStatus}
        options={p.options?.ship_statuses ?? []}
      />
      {isAnyFilterActive && (
        <button
          type="button"
          onClick={p.onReset}
          className="text-xs border border-amp-line rounded px-3 py-1.5 text-amp-ink hover:bg-amp-cream-warm dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm"
        >
          Reset filters
        </button>
      )}
    </div>
  );
}

function DateInput({
  id, label, value, onChange,
}: {
  id: string; label: string; value: string; onChange: (v: string) => void;
}) {
  return (
    <div>
      <label htmlFor={id} className="block text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted mb-1">
        {label}
      </label>
      <input
        id={id}
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="border border-amp-line rounded px-3 py-1.5 text-sm bg-amp-white text-amp-ink dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink"
      />
    </div>
  );
}

// Partner filter wraps the shared SearchableSelect. Partner is the only
// filter on this page that can exceed 15 options (the lambda caps at
// 1000), so it gets the search input per COCKPIT.md rule 2.8. Keeps the
// same label + trigger geometry as the other filters by wrapping in the
// same div shell that MultiSelect uses.
function PartnerFilter({
  value,
  onChange,
  options,
}: {
  value: string[];
  onChange: (v: string[]) => void;
  options: { value: string; label: string; sublabel?: string }[];
}) {
  return (
    <div>
      <span className="inline-flex items-baseline text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted mb-1">
        Partner
        <InfoTooltip label="Partner">
          Search by business name. The dropdown supports text search because the
          partner list can be hundreds long; other filters on this page use a
          plain scroll list.
        </InfoTooltip>
      </span>
      <SearchableSelect
        ariaLabel="Partner filter"
        options={options}
        selected={value}
        onChange={onChange}
      />
    </div>
  );
}

function MultiSelect({
  id, label, value, onChange, options,
}: {
  id: string;
  label: string;
  value: string[];
  onChange: (v: string[]) => void;
  options: { value: string; label: string }[];
}) {
  const [open, setOpen] = useState(false);
  const buttonLabel =
    value.length === 0
      ? 'Any'
      : value.length === 1
        ? options.find((o) => o.value === value[0])?.label ?? value[0]
        : `${value.length} selected`;
  return (
    <div className="relative">
      <label htmlFor={id} className="block text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted mb-1">
        {label}
      </label>
      <button
        id={id}
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="min-w-[120px] text-left border border-amp-line rounded px-3 py-1.5 text-sm bg-amp-white text-amp-ink dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink"
      >
        {buttonLabel}
      </button>
      {open && (
        <div
          className="absolute z-30 mt-1 max-h-64 w-64 overflow-y-auto border border-amp-line rounded bg-amp-white shadow-2 dark:border-amp-dark-line dark:bg-amp-dark-surface"
          onMouseLeave={() => setOpen(false)}
        >
          {options.length === 0 ? (
            <div className="px-3 py-2 text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">No options</div>
          ) : (
            options.map((o) => {
              const checked = value.includes(o.value);
              return (
                <label
                  key={o.value}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm text-amp-ink hover:bg-amp-cream-warm dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => {
                      if (checked) onChange(value.filter((v) => v !== o.value));
                      else onChange([...value, o.value]);
                    }}
                  />
                  <span className="flex-1 truncate">{o.label}</span>
                </label>
              );
            })
          )}
          {value.length > 0 && (
            <button
              type="button"
              onClick={() => onChange([])}
              className="w-full text-left px-3 py-1.5 text-xs text-amp-olive dark:text-amp-olive-dark border-t border-amp-line dark:border-amp-dark-line"
            >
              Clear
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// --- Row ---------------------------------------------------------------------

function OrderListRow({
  row,
  selected,
  onToggle,
  isAdmin,
  isEditor,
  shippingOrderId,
  onShip,
  onMatch,
}: {
  row: OrderRow;
  selected: boolean;
  onToggle: () => void;
  isAdmin: boolean;
  isEditor: boolean;
  shippingOrderId: string | null;
  onShip: (orderId: string) => void;
  onMatch: (orderId: string) => void;
}) {
  const isShipable =
    row.ship_status === 'failed' ||
    (row.ship_status === 'eligible' && row.status !== 'shipped');
  // Preserve current filters on row-click by passing the search string through
  // the to= prop. Detail's back-link relies on this.
  const detailHref = {
    pathname: `/commerce/orders/${row.order_id}`,
    search: typeof window !== 'undefined' ? window.location.search : '',
  };
  const faireUrl = faireOrderUrl(row.source_channel, row.source_order_id);
  return (
    <tr className="hover:bg-amp-cream-warm dark:hover:bg-amp-dark-surface-warm">
      {isAdmin && (
        <td className="px-2 py-2">
          <input
            type="checkbox"
            checked={selected}
            disabled={!isShipable}
            onChange={onToggle}
            aria-label={`Select ${row.source_order_id}`}
          />
        </td>
      )}
      <td className="px-2 py-2 font-mono text-xs text-amp-ink dark:text-amp-dark-ink">
        <div className="flex items-center gap-1">
          <Link to={detailHref} className="hover:underline">
            {formatOrderNumber(row.source_order_id)}
          </Link>
          {faireUrl && (
            <a
              href={faireUrl}
              target="_blank"
              rel="noopener noreferrer"
              title="Open in Faire"
              aria-label="Open in Faire brand portal"
              onClick={(e) => e.stopPropagation()}
              className="text-amp-ink-muted hover:text-amp-ink dark:text-amp-dark-ink-muted dark:hover:text-amp-dark-ink"
            >
              <ExternalLink className="h-3 w-3" aria-hidden />
            </a>
          )}
        </div>
        <div className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">
          {row.sage_so_number ?? '--'}
        </div>
      </td>
      <td className="px-2 py-2 text-amp-ink-muted dark:text-amp-dark-ink-muted">{formatDateShort(row.order_date)}</td>
      <td className="px-2 py-2 text-amp-ink-muted dark:text-amp-dark-ink-muted">{formatDateShort(row.required_ship_date)}</td>
      <td className="px-2 py-2 text-amp-ink-muted dark:text-amp-dark-ink-muted">
        <div>{formatInstantDate(row.shipped_at)}</div>
        {row.delivered_at && (
          <div className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">{formatInstantDate(row.delivered_at)}</div>
        )}
      </td>
      <td className="px-2 py-2 text-amp-ink dark:text-amp-dark-ink">{row.partner_name ?? '--'}</td>
      <td className="px-2 py-2 text-amp-ink dark:text-amp-dark-ink">{labelChannel(row.source_channel)}</td>
      <td className="px-2 py-2 text-amp-ink dark:text-amp-dark-ink">{labelBrand(row.brand)}</td>
      <td className="px-2 py-2">
        <StatusPill status={row.status} />
      </td>
      <td className="px-2 py-2">
        <ShipPill status={row.ship_status} />
      </td>
      <td className="px-2 py-2 text-right text-amp-ink dark:text-amp-dark-ink">
        {formatMoney(row.total_amount)}
      </td>
      <td className="px-2 py-2">
        <OrderActions
          row={row}
          size="sm"
          isAdmin={isAdmin}
          isEditor={isEditor}
          shippingOrderId={shippingOrderId}
          onMatch={onMatch}
          onShip={onShip}
        />
      </td>
    </tr>
  );
}

// --- Add Order modal ---------------------------------------------------------

// Today as YYYY-MM-DD in the browser's local time (matches an <input type=date>).
function todayLocal(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

function AddOrderModal({
  onClose, onCreated,
}: {
  onClose: () => void;
  onCreated: (orderId: string) => void;
}) {
  const [brand, setBrand] = useState('hammonds');
  const [partnerName, setPartnerName] = useState('');
  const [partnerType, setPartnerType] = useState<OrderRow['partner_type']>('direct');
  const [orderDate, setOrderDate] = useState(() => todayLocal());
  const [total, setTotal] = useState('');
  const [note, setNote] = useState('');
  const create = useCreateOrder();
  const [err, setErr] = useState<string | null>(null);

  const submit = () => {
    setErr(null);
    const body: CreateOrderBody = {
      brand,
      partner_name: partnerName || null,
      partner_type: partnerType,
      order_date: orderDate,
      total_amount: total ? Number(total) : null,
      note: note || null,
    };
    create.mutate(body, {
      onSuccess: (res) => onCreated(res.order_id),
      onError: (e) => setErr(e.message),
    });
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4" role="dialog">
      <div className="relative w-full max-w-md rounded-lg border border-amp-line bg-amp-white p-5 shadow-2 dark:border-amp-dark-line dark:bg-amp-dark-surface">
        <button
          type="button"
          onClick={onClose}
          className="absolute right-3 top-3 text-amp-ink-muted hover:text-amp-ink dark:text-amp-dark-ink-muted dark:hover:text-amp-dark-ink"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>
        <h2 className="text-lg font-semibold text-amp-ink dark:text-amp-dark-ink mb-1">Add Order</h2>
        <p className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted mb-4">
          Saved as a manual draft on the direct / phone channel. Push to Sage is a separate step.
        </p>
        <div className="space-y-3">
          <FieldRow label="Brand">
            <select
              value={brand}
              onChange={(e) => setBrand(e.target.value)}
              className="w-full border border-amp-line rounded px-3 py-1.5 text-sm bg-amp-white text-amp-ink dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink"
            >
              <option value="hammonds">Hammond's</option>
              <option value="lolli_pops">Lolli & Pops</option>
              <option value="ampersand">Ampersand</option>
            </select>
          </FieldRow>
          <FieldRow label="Partner name">
            <input
              type="text"
              value={partnerName}
              onChange={(e) => setPartnerName(e.target.value)}
              placeholder="Retailer or guest name"
              className="w-full border border-amp-line rounded px-3 py-1.5 text-sm bg-amp-white text-amp-ink dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink"
            />
          </FieldRow>
          <FieldRow label="Partner type">
            <select
              value={partnerType}
              onChange={(e) => setPartnerType(e.target.value as OrderRow['partner_type'])}
              className="w-full border border-amp-line rounded px-3 py-1.5 text-sm bg-amp-white text-amp-ink dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink"
            >
              <option value="direct">Direct / guest</option>
              <option value="retailer">Retailer</option>
              <option value="private_label">Private label retailer</option>
              <option value="broker">Broker</option>
              <option value="guest">Guest</option>
            </select>
          </FieldRow>
          <FieldRow label="Order date">
            <input
              type="date"
              value={orderDate}
              onChange={(e) => setOrderDate(e.target.value)}
              className="w-full border border-amp-line rounded px-3 py-1.5 text-sm bg-amp-white text-amp-ink dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink"
            />
          </FieldRow>
          <FieldRow label="Total amount">
            <input
              type="number"
              step="0.01"
              value={total}
              onChange={(e) => setTotal(e.target.value)}
              placeholder="0.00"
              className="w-full border border-amp-line rounded px-3 py-1.5 text-sm bg-amp-white text-amp-ink dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink"
            />
          </FieldRow>
          <FieldRow label="Note">
            {/* Stays a plain box. This note is stored inside a JSON metadata blob,
                not its own column, and the same create path is reused by the Connect
                partner portal. Nothing sanitizes HTML here either. */}
            <textarea
              rows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="w-full border border-amp-line rounded px-3 py-1.5 text-sm bg-amp-white text-amp-ink dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink"
            />
          </FieldRow>
        </div>
        {err && <p className="mt-3 text-xs text-red-700 dark:text-red-300">{err}</p>}
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="text-xs border border-amp-line rounded px-3 py-1.5 text-amp-ink hover:bg-amp-cream-warm dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={create.isPending}
            className="text-xs border border-amp-ink bg-amp-ink text-amp-white rounded px-3 py-1.5 hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60 dark:border-amp-dark-ink dark:bg-amp-dark-ink dark:text-amp-dark-surface inline-flex items-center gap-1"
          >
            {create.isPending && <Loader2 className="h-3 w-3 animate-spin" />}
            Save draft
          </button>
        </div>
      </div>
    </div>
  );
}

function FieldRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted mb-1">{label}</label>
      {children}
    </div>
  );
}

function AccessDeniedCard() {
  return (
    <div className="max-w-xl rounded-lg border border-amp-line p-5 dark:border-amp-dark-line">
      <h2 className="text-lg font-semibold text-amp-ink dark:text-amp-dark-ink mb-1">No access</h2>
      <p className="text-sm text-amp-ink dark:text-amp-dark-ink">
        Commerce Orders is gated by the cockpit-faire-sage-viewer Entra group. Ask IT to add you.
      </p>
    </div>
  );
}
